"use client";

import { useEffect, useState } from "react";
import { Box, Stack, SimpleGrid, Text, TextInput, MultiSelect, Group, Paper, rem } from "@mantine/core";
import { motion } from "framer-motion";
import FAIcon from "@/components/FAIcon";
import Card from "@/components/Card";
import type { BlogPost } from "@/services/blogService";

interface FilterState {
    search: string;
    categories: string[];
    tags: string[];
}

interface ResourcesContentProps {
    initialPosts: BlogPost[];
    allCategories: { label: string; value: string }[];
    allTags: { label: string; value: string }[];
}

export default function ResourcesContent({ initialPosts, allCategories, allTags }: ResourcesContentProps) {
    const [posts] = useState<BlogPost[]>(initialPosts);
    const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(initialPosts);
    const [filters, setFilters] = useState<FilterState>({ search: "", categories: [], tags: [] });

    // Filter out any empty values from categories and tags
    const safeCategories = allCategories.filter(cat => cat.value && cat.value.trim() !== '');
    const safeTags = allTags.filter(tag => tag.value && tag.value.trim() !== '');

    useEffect(() => {
        try {
            const filtered = posts.filter((post) => {
                const matchesSearch =
                    !filters.search ||
                    post.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                    (post.description && post.description.toLowerCase().includes(filters.search.toLowerCase()));
                const matchesCategories =
                    filters.categories.length === 0 ||
                    (post.categories && filters.categories.some((cat) => post.categories?.includes(cat)));
                const matchesTags =
                    filters.tags.length === 0 ||
                    (post.tags && filters.tags.some((tag) => post.tags?.includes(tag)));
                return matchesSearch && matchesCategories && matchesTags;
            });
            setFilteredPosts(filtered);
        } catch (error) {
            setFilteredPosts(posts);
        }
    }, [filters, posts]);

    const handleCategoryChange = (value: string[]) => {
        setFilters((prev) => ({ ...prev, categories: value }));
    };

    const handleTagChange = (value: string[]) => {
        setFilters((prev) => ({ ...prev, tags: value }));
    };

    return (
        <Paper shadow="sm" p="xl" mb={40} radius="md" style={{ backdropFilter: "blur(8px)" }}>
            <Stack gap="lg" p={0}>
                <TextInput
                    placeholder="Search resources..."
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                    size="lg"
                    radius="md"
                    leftSection={<FAIcon name="search" size={20} />}
                    styles={{
                        input: {
                            transition: "box-shadow 200ms ease",
                            "&:focus": {
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                            },
                        },
                    }}
                />
                <Group gap="md" align="start" grow>
                    <MultiSelect
                        data={safeCategories}
                        placeholder="Filter by categories"
                        value={filters.categories}
                        onChange={handleCategoryChange}
                        clearable
                        searchable
                        leftSection={<FAIcon name="clone" size={16} />}
                    />
                    <MultiSelect
                        data={safeTags}
                        placeholder="Filter by tags"
                        value={filters.tags}
                        onChange={handleTagChange}
                        clearable
                        searchable
                        leftSection={<FAIcon name="tag" size={16} />}
                    />
                </Group>
            </Stack>
            <Box mt="xl">
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing={40}>
                    {filteredPosts.map((post, index) => (
                        <motion.div
                            key={post.slug}
                            initial={{ transform: "translateY(20px)", opacity: 0 }}
                            whileInView={{ transform: "translateY(0)", opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.25 }}
                        >
                            <Card
                                pb={30}
                                image={post.image || ""}
                                title={post.title}
                                content={post.description || ""}
                                date={post.date}
                                href={`/resources/${post.slug}`}
                                aria-label={post.title}
                            />
                        </motion.div>
                    ))}
                </SimpleGrid>
                {filteredPosts.length === 0 && (
                    <Box ta="center" py={60}>
                        <FAIcon name="search-minus" size={48} style={{ marginBottom: rem(20), opacity: 0.5 }} />
                        <Text size="xl" fw={500} mb={rem(10)}>
                            No resources found
                        </Text>
                        <Text c="dimmed">Try adjusting your search or filters</Text>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}
